import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { Button, Typography } from "@mui/material";
import { PageContainer } from "@/features/ui";
import dayjs from "dayjs";

import {
  DEFAULT_VALUES,
  JournalVoucherForm,
} from "@/features/journal-vouchers";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditJournalVoucher = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [journalVoucher, setJournalVoucher] = useState(null);
  const [error, setError] = useState(null);

  const loadJournalVoucher = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`journal-vouchers/${referenceNumber}`, {
        params: { withOutlet: true },
      });
      setJournalVoucher(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memperbarui jurnal")
      );
    } finally {
      setLoading(false);
    }
  }, [referenceNumber]);

  useEffect(() => {
    loadJournalVoucher();
  }, [loadJournalVoucher]);

  // Transform journal voucher data to form default values
  const defaultValues = useMemo(() => {
    if (!journalVoucher) return DEFAULT_VALUES;

    return {
      outlet: journalVoucher.outlet,
      transactionDate: dayjs(journalVoucher.transactionDate),
      note: journalVoucher.note || "",
      outletChartOfAccountTransactions:
        journalVoucher.outletChartOfAccountTransactions.map((transaction) => ({
          outletChartOfAccount: transaction.outletChartOfAccount,
          debit: transaction.amount > 0 ? transaction.amount : 0,
          credit: transaction.amount < 0 ? Math.abs(transaction.amount) : 0,
          note: transaction.note || "",
          entityType: transaction.entityType || null,
          entity: transaction.supplier || transaction.customer || transaction.outlet || null,
        })),
    };
  }, [journalVoucher]);

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      const { outletId, ...payload } = data;

      await apiOutletClient.put(
        `journal-vouchers/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": outletId,
          },
        }
      );

      showSnackbar("Jurnal umum berhasil diperbarui", "success");
      navigate(`/general-ledger/journal-vouchers/${referenceNumber}`);
    } catch (error) {
      console.error("Failed to update journal voucher:", error);
      showSnackbar(
        getErrorMessage(error, "Gagal memperbarui jurnal") ||
          "Gagal memperbarui jurnal umum"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Edit Jurnal Umum"
      breadcrumbs={[
        { title: "Jurnal Umum", path: "/general-ledger/journal-vouchers" },
        {
          title: referenceNumber,
          path: `/general-ledger/journal-vouchers/${referenceNumber}`,
        },
        { title: "Edit" },
      ]}
    >
      <JournalVoucherForm
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isBalanced }) => (
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isBalanced}
            size="large"
          >
            {isSubmitting ? "Menyimpan..." : "Simpan"}
          </Button>
        )}
      />
    </PageContainer>
  );
};
